import { SERVICES_URL, handleError } from "api/helper";
import { del, get, post, put } from "api/http";
import { ReadLayerResult } from "./readLayerService";
import { ProgramService } from "@adfenix/types";

const baseAuthUrlV1 = "program/auth/v1";
const baseAuthUrlV2 = "program/auth/v2";
const baseAuthUrlV3 = "program/auth/v3";

export const getProgramOverview = (
  hqId?: string
): Promise<ReadLayerResult<ProgramService.ProgramSearchModel>> =>
  post(`${SERVICES_URL}/${baseAuthUrlV1}/program/search`, {
    filters: [{ column: "hq.id", value: hqId }],
    pageSize: 1000
  });

export const getProgramDetails = (
  programId: string
): Promise<ProgramService.ProgramModel> =>
  get(`${SERVICES_URL}/${baseAuthUrlV1}/program/${programId}`).catch(
    handleError
  );

export const getProgramInstanceOverview = (): Promise<
  ReadLayerResult<ProgramService.ProgramInstanceSearchModel>
> =>
  post(`${SERVICES_URL}/${baseAuthUrlV1}/program-instance/search`, {
    pageSize: 1000,
    page: 1
  });

export const getProgramInstanceDetails = (
  programId: string
): Promise<ProgramService.ProgramInstanceDetailsModel> =>
  get(`${SERVICES_URL}/${baseAuthUrlV1}/program-instance/${programId}`).catch(
    handleError
  );

export const getProgramInstanceOrderPage = (
  programInstanceId: string
): Promise<ProgramService.ProgramInstanceOrderPageModelV2> =>
  get(
    `${SERVICES_URL}/${baseAuthUrlV2}/program-instance/${programInstanceId}/customer-order-page`
  );

export const getMapTestOrderPage = (
  subjectId: string,
  programId: string,
  officeId: string,
  subjectType: string
): Promise<ProgramService.ProgramInstanceOrderPageModelV2> =>
  get(
    `${SERVICES_URL}/${baseAuthUrlV3}/program-instance/test-order-page/${programId}/${officeId}/${subjectType}/${subjectId}/formatted-content`
  );

export const getTestOrderPage = (
  subjectId: string,
  programId: string,
  officeId: string,
  subjectType: string
): Promise<ProgramService.ProgramInstanceOrderPageModelV2> =>
  get(
    `${SERVICES_URL}/${baseAuthUrlV3}/program-instance/test-order-page/${programId}/${officeId}/${subjectType}/${subjectId}`
  );

export const updateProgram = ({
  program
}: {
  program: ProgramService.ProgramModel;
}): Promise<void> =>
  put(`${SERVICES_URL}/${baseAuthUrlV1}/program`, program).catch(handleError);

export const activateProgram = ({
  programId
}: {
  programId: string;
}): Promise<void> =>
  put(`${SERVICES_URL}/${baseAuthUrlV1}/program/${programId}/activate`).catch(
    handleError
  );

export const requestActivation = ({
  programId
}: {
  programId: string;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program/${programId}/request-activation`
  ).catch(handleError);

export const deactivateProgram = ({
  programId
}: {
  programId: string;
}): Promise<void> =>
  put(`${SERVICES_URL}/${baseAuthUrlV1}/program/${programId}/deactivate`).catch(
    handleError
  );

export const duplicateProgram = ({
  programId,
  hqId
}: {
  hqId: string;
  programId: string;
}): Promise<string> =>
  post(`${SERVICES_URL}/${baseAuthUrlV1}/program/duplicate`, {
    hqId,
    programId
  }).catch(handleError);

export const createProgram = ({
  hqId,
  name,
  subjectType
}: {
  hqId: string;
  name: string;
  subjectType: ProgramService.SubjectType;
}): Promise<string> =>
  post(`${SERVICES_URL}/${baseAuthUrlV1}/program/create`, {
    hqId,
    name,
    subjectType
  }).catch(handleError);

export const deleteProgram = ({
  programId
}: {
  programId: string;
}): Promise<void> =>
  del(`${SERVICES_URL}/${baseAuthUrlV1}/program/${programId}`).catch(
    handleError
  );

export const getAvailableAttributes = (
  programId: string
): Promise<
  {
    subjectType: ProgramService.SubjectType;
    name: string;
  }[]
> =>
  get(
    `${SERVICES_URL}/${baseAuthUrlV1}/program/${programId}/available-attributes`
  ).catch(handleError);

export const getAvailableProgramInstances = (
  subjectType: ProgramService.SubjectType,
  id: string
): Promise<ProgramService.OrderPageOverviewModel[]> => {
  return get(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/available-programs/${subjectType.toLowerCase()}/${id}`
  ).catch(handleError);
};

export const getCanTriggerPrograms = (
  hqId: string,
  subjectType: ProgramService.SubjectType
): Promise<ProgramService.ProgramItem[]> => {
  return post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program/get-can-trigger-programs`,
    {
      hqId,
      subjectType
    }
  ).catch(handleError);
};

export const getAvailableTriggerEvents = (hqId: string): Promise<string[]> => {
  return get(
    `${SERVICES_URL}/${baseAuthUrlV1}/program/available-trigger-events?hqId=${hqId}`
  ).catch(handleError);
};

export const getAvailableTriggerEventsV2 = (
  hqId: string
): Promise<ProgramService.SubjectTriggerEventModel[]> => {
  return get(
    `${SERVICES_URL}/${baseAuthUrlV2}/program/available-trigger-events/${hqId}`
  ).catch(handleError);
};

export const getSubjectIdForProgramInstance = (
  programInstanceId: string
): Promise<string> => {
  return get(
    `${SERVICES_URL}/program/public/v1/program-instance/${programInstanceId}/subject-id`
  ).catch(handleError);
};

export const retryOfficeActivation = (
  programInstanceId: string
): Promise<void> => {
  return post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/retry-office-active/${programInstanceId}`
  ).catch(handleError);
};

export const recheckConditions = (programInstanceId: string): Promise<void> => {
  return post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/recheck-conditions/${programInstanceId}`
  ).catch(handleError);
};

export const retryBlockedInstancesForProgram = (
  programId: string
): Promise<void> => {
  return post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/retry-blocked-workflows-for-program/${programId}`
  ).catch(handleError);
};

export const getAvailableCampaignTemplates = (
  bundleId: string
): Promise<ProgramService.AvailableCampaignTemplate[]> => {
  return get(
    `${SERVICES_URL}/${baseAuthUrlV1}/program/bundle/${bundleId}/available-campaign-templates`
  ).catch(handleError);
};

export const getProgramSummary = (
  subjectType: ProgramService.SubjectType,
  subjectId: string
): Promise<ProgramService.OrderPageSummaryModel> => {
  return get(
    `${SERVICES_URL}/${baseAuthUrlV1}/order-page/available-on-subject/${subjectType.toLowerCase()}/${subjectId}`
  ).catch(handleError);
};

export const createOrderPage = (
  programId: string,
  subjectId: string
): Promise<string> => {
  return post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/try-create/${programId}/${subjectId}`
  ).catch(handleError);
};

export const sendManualPublicationOffer = (
  programId: string
): Promise<void> => {
  return post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/send-manual-publication-offer/${programId}`
  ).catch(handleError);
};

export const resendSellersChoiceStart = (
  programInstanceId: string
): Promise<void> => {
  return post(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/resend-start-sellers-choice/${programInstanceId}`
  ).catch(handleError);
};

export const createCustomTrigger = ({
  hqId,
  name,
  targetSubjectType
}: {
  hqId: string;
  name: string;
  targetSubjectType: ProgramService.SubjectType;
}) =>
  post(
    `${SERVICES_URL}/program/auth/v1/triggers/${hqId}?targetSubjectType=${targetSubjectType}`,
    name
  ).catch(handleError);

export const renameCustomTrigger = ({
  hqId,
  triggerId,
  newName
}: {
  hqId: string;
  triggerId: string;
  newName: string;
}) =>
  post(
    `${SERVICES_URL}/program/auth/v1/triggers/${hqId}/${triggerId}`,
    newName
  ).catch(handleError);

export const getCustomTrigger = ({
  hqId
}: {
  hqId: string;
}): Promise<ProgramService.CustomTriggerModel[]> =>
  get(`${SERVICES_URL}/program/auth/v1/triggers/${hqId}`).catch(handleError);

export const getSubscriptionBundles = (
  subjectType: ProgramService.SubjectType,
  subjectId: string
): Promise<ProgramService.SubscriptionBundleModel[]> => {
  return get(
    `${SERVICES_URL}/${baseAuthUrlV1}/order-page/subscription-bundles/${subjectType}/${subjectId}`
  ).catch(handleError);
};

export const getProgramAuditLog = (
  id: string
): Promise<ProgramService.ProgramAuditLogModel[]> => {
  return get(`${SERVICES_URL}/${baseAuthUrlV1}/program/${id}/audit-log`).catch(
    handleError
  );
};

export const getPropertiesNonAdminsCanEdit = (): Promise<string[]> => {
  return get(
    `${SERVICES_URL}/${baseAuthUrlV1}/program/properties-non-admins-can-edit`
  ).catch(handleError);
};

export const getSellerPaidOrderData = (
  programInstanceId: string
): Promise<ProgramService.SellerPaidOrderPageModel> =>
  get(
    `${SERVICES_URL}/${baseAuthUrlV1}/order-page/seller/on-program-instance/${programInstanceId}`
  ).catch(handleError);

export const getHqInfo = (
  programInstanceId: string
): Promise<{
  hqId: string;
}> =>
  get(
    `${SERVICES_URL}/${baseAuthUrlV1}/program-instance/${programInstanceId}/hq-info`
  ).catch(handleError);
